import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import React, { Suspense } from "react";

// Import authentication system
import { AuthProvider } from "@/features/auth";
import { AuthDebugPanel } from "@/features/auth/components/AuthDebugPanel";

// Pages
import Index from "./pages/Index";
import AccomodationPage from "@/features/alojamientos/pages/AccomodationPage";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import EditEstablishment from "./pages/EditEstablishment";
import DetailEstablishment from "./pages/DetailEstablishment";
import SearchPage from "./pages/SearchPage";
import DailySamples from "./pages/DailySamples";
import Dashboard from "./pages/Dashboard";
import News from "./pages/News";

// Dashboard feature
import { StatsDashboardPage } from "@/features/dashboard";
import {
  MunicipalListFormPage,
  MunicipalListsPage,
  PreRegisteredAccommodationsPage,
  PRE_REGISTRATION_EDIT_ROLES,
  PRE_REGISTRATION_VIEW_ROLES,
  preRegistrationRoutes,
} from "@/features/pre-registrations";
import Unauthorized from "./pages/Unauthorized";
import AssignSample from "./pages/AssignSample";

// Lazy-loaded components
const CreateSamples = React.lazy(() => import('./pages/CreateSamples'));
const SearchSamples = React.lazy(() => import('./pages/SearchSamples'));

// Authentication components
import { ProtectedRoute } from '@/features/auth';
import { ROLES } from '@/features/auth/constants/roles';

// Features
import { EmployeeCreatePage } from "@/features/employees";

const queryClient = new QueryClient();

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Auth />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/estadisticas" element={
              <ProtectedRoute>
                <StatsDashboardPage />
              </ProtectedRoute>
            } />
            
            <Route path="/news" element={
              <ProtectedRoute>
                <News />
              </ProtectedRoute>
            } />

            <Route path="/muestras/completar" element={
              <ProtectedRoute>
                <DailySamples />
              </ProtectedRoute>
            } />

            <Route path="/accommodation" element={
              <ProtectedRoute>
                <AccomodationPage />
              </ProtectedRoute>
            } />
            
            <Route path="/accommodation/:id" element={
              <ProtectedRoute>
                <AccomodationPage />
              </ProtectedRoute>
            } />

            <Route path="/establecimientos/editar/:id" element={
              <ProtectedRoute requiredRole={[ROLES.FISCALIZACION_EDITOR, ROLES.ADMIN]}>
                <EditEstablishment />
              </ProtectedRoute>
            } />

            <Route path="/muestras/crear" element={
              <ProtectedRoute>
                <Suspense fallback={<div>Loading...</div>}>
                  <CreateSamples />
                </Suspense>
              </ProtectedRoute>
            } />
            
            <Route path="/muestras/buscar" element={
              <ProtectedRoute>
                <Suspense fallback={<div>Loading...</div>}>
                  <SearchSamples />
                </Suspense>
              </ProtectedRoute>
            } />
            
            <Route path="/muestras/asignar" element={
              <ProtectedRoute>
                <AssignSample />
              </ProtectedRoute>
            } />
            
            <Route path="/establecimientos/:id" element={
              <ProtectedRoute>
                <DetailEstablishment />
              </ProtectedRoute>
            } />
            
            <Route path="/buscador" element={
              <ProtectedRoute>
                <SearchPage />
              </ProtectedRoute>
            } />
            
            <Route path="/preinscripciones" element={
              <Navigate to={preRegistrationRoutes.accommodations} replace />
            } />

            <Route path={preRegistrationRoutes.accommodations} element={
              <ProtectedRoute requiredRole={PRE_REGISTRATION_VIEW_ROLES}>
                <PreRegisteredAccommodationsPage />
              </ProtectedRoute>
            } />

            <Route path={preRegistrationRoutes.municipalLists} element={
              <ProtectedRoute requiredRole={PRE_REGISTRATION_VIEW_ROLES}>
                <MunicipalListsPage />
              </ProtectedRoute>
            } />

            <Route path={preRegistrationRoutes.newMunicipalList} element={
              <ProtectedRoute requiredRole={PRE_REGISTRATION_EDIT_ROLES}>
                <MunicipalListFormPage />
              </ProtectedRoute>
            } />

            <Route path={preRegistrationRoutes.municipalList(':listId')} element={
              <ProtectedRoute requiredRole={PRE_REGISTRATION_VIEW_ROLES}>
                <MunicipalListFormPage />
              </ProtectedRoute>
            } />

            <Route path="/employee/create" element={
              <ProtectedRoute>
                <EmployeeCreatePage />
              </ProtectedRoute>
            } />

            {/* Catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          
          {/* Debug Panel - Solo en desarrollo */}
          {/* <AuthDebugPanel show={false} /> */}
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
